/* eslint-disable react/jsx-no-useless-fragment */
import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Action,
  Avatar, Card, CardSkeleton, DetailItemList, FontWeight, HStack, ICON_ALERT_CIRCLE_OUTLINE, ICON_BRIEFCASE_OUTLINE, ICON_CALENDAR_OUTLINE, ICON_PHONE_OFF_OUTLINE, ICON_PHONE_OUTLINE, Message, Stack, Text,
  UserPreview,
  UserPreviewSkeleton,
} from '@doveit/bricks';
import { formatDate } from '@doveit/hammer';
import RightColumnPageLayout from '../../../layouts/right-column-page-layout/RightColumnPageLayout';
import { useJobApplication } from '../../hooks/use-job-application/useJobApplication';
import useJobPosition from '../../../job-position/hooks/use-job-position/useJobPosition';
import RightColumnPageSkeleton from '../../../components/page-skeleton/RightColumnPageSkeleton';
import GenericErrorPage from '../../../pages/errors/generic/GenericErrorPage';
import { jobApplicationTypeLabels } from '../../../labels/jobApplicationTypeLabels';
import { JobApplicationStatus, JobApplicationType, ReferenceType } from '../../../domain/types';
import UpdateAssignedAgentAction from '../../../agent/containers/update-assigned-agent-action/UpdateAssignedAgentAction';
import { updateJobApplication } from '../../../providers/api/job-application/jobApplicationProvider';
import { ViewAgentSmallPreview } from '../../../agent/components';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { isActive } from '../../../utils/job-application/getJobApplicationProgress';
import UpdateSupervisorAction from '../../containers/update-supervisor-action/UpdateSupervisorAction';
import { useApplicant } from '../../../applicant/hooks/use-applicant/useApplicant';
import UpdateJobApplicationAction from '../../containers/update-job-application-action/UpdateJobApplicationAction';
import NoAnswerAction from '../../containers/no-answer-action/NoAnswerAction';
import UpdateApplicantAction from '../../../applicant/container/update-applicant-action/UpdateApplicantAction';
import NotesWidget from '../../../notes/containers/notes-widget/NotesWidget';
import UpdateJobApplicationStatus from '../../containers/update-job-application-status/UpdateJobApplicationStatus';
import UpsertHRInterviewAction from '../../containers/upsert-hr-interview-action/UpsertHRInterviewAction';
import { useHRCalendarEvents } from '../../../hr/hooks/use-hr-calendar-events/useHRCalendarEvents';
import UpsertHRRecallAction from '../../../hr/containers/upsert-hr-recall-action/UpsertHRRecallAction';
import ApplicantOptInsWidget from '../../../applicant/container/applicant-optins-widget/ApplicantOptInsWidget';
import ApplicantMissedCallRegistry from '../../components/applicant-missed-call-registry/ApplicantMissedCallRegistry';
import UserActions from '../../../components/user-actions/UserActions';
import HRCalendarEventsWidget from '../../../hr/containers/hr-calendar-events-widget/HRCalendarEventsWidget';
import UpdateAssignedUserAction from '../../components/update-assigned-user-action/UpdateAssignedUserAction';
import { JobApplication } from '../../../providers/api/dtos/jobApplication';
import { jobApplicationAssigneeUsers, jobApplicationHrUsers } from '../../constants';
import useJobApplicationNoticesData from '../../hooks/use-job-application-notices-data/useJobApplicationNoticesData';
import NoticeBoard from '../../../notice/components/notice-board/NoticeBoard';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { buildJobApplicationNotice } from '../../../utils/notice/buildJobApplicationNotice';

export const LOAD_JOB_APPLICATION_ERROR_MESSAGE = 'Non è stato possibile caricare i dati della candidatura.';
export const LOAD_APPLICANT_ERROR_MESSAGE = 'Non è stato possibile caricare i dati dei candidato.';

const ViewJobApplicationPage: React.FC = () => {
  const { id } = useParams();
  const isMobile = useIsDevice('mobile');

  const { userIsAdmin, userIsHR, user } = useRBAC();

  const {
    data: jobApplication, isLoading: isJobApplicationLoading, error: jobApplicationError, mutate: mutateJobApplication,
  } = useJobApplication(id);

  const {
    mutate: mutateCalendarEvents,
  } = useHRCalendarEvents({
    jobApplicationId: id,
  });

  const {
    data: applicant,
    isLoading: isApplicantLoading,
    error: applicantError,
    mutate: mutateApplicant,
  } = useApplicant(jobApplication?.applicantId);

  const userIsAssignee = React.useMemo(
    () => user.email === jobApplication?.assigneeEmail,
    [jobApplication?.assigneeEmail, user.email],
  );

  const {
    data: noticesData = [],
    isLoading: areNoticesDataLoading,
    errors: noticesDataErrors,
    mutate: mutateNoticesData,
  } = useJobApplicationNoticesData(jobApplication);

  const { data: jobPosition, isLoading: isJobPositionLoading, error: jobPositionError } = useJobPosition(jobApplication?.jobPositionId);

  const isLoading = React.useMemo(() => isJobApplicationLoading || isJobPositionLoading, [isJobApplicationLoading, isJobPositionLoading]);
  const isError = React.useMemo(() => jobApplicationError || jobPositionError, [jobApplicationError, jobPositionError]);

  const canUpdateSupervisor = React.useMemo(() => {
    if (!jobApplication) return false;

    return (userIsAdmin || userIsHR) && isActive(jobApplication);
  }, [jobApplication, userIsAdmin, userIsHR]);

  const canUpdateAssignee = React.useMemo(() => {
    if (!jobApplication) return false;

    return (userIsAdmin || userIsHR) && isActive(jobApplication);
  }, [jobApplication, userIsAdmin, userIsHR]);

  const canUpdateReferral = React.useMemo(() => {
    if (!jobApplication) return false;

    return (userIsAdmin || userIsHR) && isActive(jobApplication);
  }, [jobApplication, userIsAdmin, userIsHR]);

  const canViewApplicant = React.useMemo(
    () => (userIsAdmin || userIsHR || userIsAssignee),
    [userIsAdmin, userIsAssignee, userIsHR],
  );

  const canManageCalendarEvents = React.useMemo(() => userIsAdmin || userIsHR, [userIsAdmin, userIsHR]);

  const showCalendarTools = React.useMemo(
    () => jobApplication && canManageCalendarEvents && [JobApplicationStatus.IN_PROGRESS, JobApplicationStatus.ASSIGNED].includes(jobApplication?.status),
    [canManageCalendarEvents, jobApplication],
  );

  const canManageApplicant = React.useMemo(
    () => userIsAdmin || userIsHR,
    [userIsAdmin, userIsHR],
  );

  const getUserName = (email?: string) => {
    if (!email) return undefined;
    const users = [...jobApplicationAssigneeUsers, ...jobApplicationHrUsers];
    return users.find((interviewerUser) => interviewerUser.email === email)?.name
      || users.find((assigneeUser) => assigneeUser.email === email)?.name || email;
  };

  const supervisorPicture = React.useMemo(() => {
    if (!jobApplication?.supervisorEmail) return undefined;
    return jobApplicationHrUsers.find((u) => u.email === jobApplication?.supervisorEmail)?.picture;
  }, [jobApplication?.supervisorEmail]);

  const notices = React.useMemo(() => {
    if (!applicant) return [];

    return noticesData.map((noticeData) => buildJobApplicationNotice(noticeData, {
      isMobile,
      applicant,
      mutateNoticeData: mutateNoticesData,
    }));
  }, [applicant, noticesData, isMobile, mutateNoticesData]);

  if (isError || (!isLoading && (!jobApplication || !jobPosition))) {
    return <GenericErrorPage title={LOAD_JOB_APPLICATION_ERROR_MESSAGE} />;
  }

  if (isLoading) {
    return <RightColumnPageSkeleton />;
  }

  return jobApplication && jobPosition && (
    <RightColumnPageLayout
      title={(
        <HStack gap={200} wrap="nowrap">
          <Avatar
            placeholder={ICON_BRIEFCASE_OUTLINE}
          />
          <div>
            <div>
              <Text.Body
                fontWeight={FontWeight.REGULAR}
                as="div"
              >
                {applicant?.name}
              </Text.Body>
            </div>
            <Text.BodySmall as="div">
              {jobPosition.name}
            </Text.BodySmall>
          </div>
        </HStack>
      )}
      sidePrimaryContent={(
        <Card>
          <Card.Header>
            <UpdateJobApplicationStatus
              jobApplication={jobApplication}
              showIcon
              emphasis="high"
              onSuccess={() => {
                mutateJobApplication();
                mutateApplicant();
                mutateCalendarEvents();
              }}
            />
          </Card.Header>
          <Card.Content aria-label="Sezione stato candidatura">
            <DetailItemList>
              <DetailItemList.Item
                label="Data creazione"
                aria-label="Data creazione"
              >
                {formatDate(new Date(jobApplication.createdAt!))}
              </DetailItemList.Item>
              <DetailItemList.Item
                label="Data aggiornamento"
                aria-label="Data aggiornamento"
              >
                {formatDate(new Date(jobApplication.updatedAt!))}
              </DetailItemList.Item>
              <DetailItemList.Item
                label="Sorgente"
                aria-label="Sorgente"
              >
                {jobApplication.source}
              </DetailItemList.Item>
              <DetailItemList.Item
                label="Gestore HR"
                aria-label="Gestore HR"
                fullWidth
              >
                {canUpdateSupervisor && (
                  <UpdateSupervisorAction
                    jobApplication={jobApplication}
                    onSuccess={() => mutateJobApplication()}
                  >
                    {({ openUpdateModal, isSaving }) => {
                      if (!jobApplication.supervisorEmail) {
                        return (
                          <UserPreview
                            name="Non presente"
                            size="S"
                            onClick={openUpdateModal}
                            expanded
                          />
                        );
                      }

                      if (isSaving) {
                        return <UserPreviewSkeleton size="S" />;
                      }

                      return (
                        <UserPreview
                          aria-label="user preview editabile del supevisor"
                          name={getUserName(jobApplication.supervisorEmail)}
                          size="S"
                          onClick={openUpdateModal}
                          expanded
                          avatar={supervisorPicture ? { src: supervisorPicture } : undefined}
                        />
                      );
                    }}
                  </UpdateSupervisorAction>
                )}

                {!canUpdateSupervisor && (
                  <UserPreview
                    aria-label="user preview non editabile del supevisor"
                    name={getUserName(jobApplication.supervisorEmail) || 'Non presente'}
                    size="S"
                    expanded
                    avatar={supervisorPicture ? { src: supervisorPicture } : undefined}
                  />
                )}
              </DetailItemList.Item>

              <DetailItemList.Item
                label="Assegnatario"
                aria-label="Assegnatario"
                fullWidth
              >
                {canUpdateAssignee && jobApplication.applicationType === JobApplicationType.SALES && (
                  <UpdateAssignedAgentAction<JobApplication>
                    label="Assegnatario"
                    reference={jobApplication}
                    isRequired
                    getAgentReference={({ assigneeEmail }) => assigneeEmail}
                    onUpdate={({ assignee }) => updateJobApplication(id!, {
                      ...jobApplication,
                      assigneeEmail: assignee?.email.work,
                      isFranchising: assignee?.franchising ?? false,
                      status: (jobApplication.status === JobApplicationStatus.IN_PROGRESS) && assignee?.email.work ? JobApplicationStatus.ASSIGNED : jobApplication.status,
                    })}
                    onSuccess={() => mutateJobApplication()}
                  >
                    {({ openUpdateModal, isSaving }) => {
                      if (!jobApplication.assigneeEmail) {
                        return (
                          <UserPreview
                            name="Non presente"
                            size="S"
                            onClick={openUpdateModal}
                            expanded
                          />
                        );
                      }

                      if (isSaving) {
                        return <UserPreviewSkeleton size="S" />;
                      }

                      return (
                        <ViewAgentSmallPreview
                          agentIdOrEmail={jobApplication.assigneeEmail}
                          size="S"
                          onClick={openUpdateModal}
                          expanded
                        />
                      );
                    }}
                  </UpdateAssignedAgentAction>
                )}

                {canUpdateAssignee && jobApplication.applicationType === JobApplicationType.GENERIC && (
                  <UpdateAssignedUserAction
                    label="Assegnatario"
                    aria-label="Campo per inserire l\'assegnatario"
                    email={jobApplication.assigneeEmail}
                    isRequired
                    onUpdate={({ email }) => updateJobApplication(id!, {
                      ...jobApplication,
                      assigneeEmail: email,
                      status: (jobApplication.status === JobApplicationStatus.IN_PROGRESS) && email ? JobApplicationStatus.ASSIGNED : jobApplication.status,
                    })}
                    onSuccess={() => mutateJobApplication()}
                  >
                    {({ openUpdateModal, isSaving }) => {
                      if (!jobApplication.assigneeEmail) {
                        return (
                          <UserPreview
                            name="Non presente"
                            size="S"
                            onClick={openUpdateModal}
                            expanded
                          />
                        );
                      }

                      if (isSaving) {
                        return <UserPreviewSkeleton size="S" />;
                      }

                      return (
                        <UserPreview
                          aria-label="user preview editabile"
                          name={getUserName(jobApplication.assigneeEmail) || 'Non presente'}
                          onClick={openUpdateModal}
                          size="S"
                          expanded
                        />
                      );
                    }}
                  </UpdateAssignedUserAction>
                )}

                {!canUpdateAssignee && jobApplication.applicationType === JobApplicationType.SALES && jobApplication.assigneeEmail && (
                  <ViewAgentSmallPreview
                    agentIdOrEmail={jobApplication.assigneeEmail}
                    size="S"
                    expanded
                  />
                )}

                {!canUpdateAssignee && (!jobApplication.assigneeEmail || jobApplication.applicationType === JobApplicationType.GENERIC) && (
                  <UserPreview
                    aria-label="user preview non editabile"
                    name={getUserName(jobApplication.assigneeEmail) || 'Non presente'}
                    size="S"
                    expanded
                  />
                )}
              </DetailItemList.Item>

              <DetailItemList.Item
                label="Segnalatore"
                aria-label="Segnalatore"
                fullWidth
              >
                {canUpdateReferral && jobApplication.applicationType === JobApplicationType.SALES && (
                  <UpdateAssignedAgentAction<JobApplication>
                    label="Segnalatore"
                    reference={jobApplication}
                    getAgentReference={({ referralEmail }) => referralEmail}
                    onUpdate={({ assignee }) => updateJobApplication(id!, {
                      ...jobApplication,
                      referralEmail: assignee?.email.work,
                    })}
                    onSuccess={() => mutateJobApplication()}
                  >
                    {({ openUpdateModal, isSaving }) => {
                      if (!jobApplication.referralEmail) {
                        return (
                          <UserPreview
                            name="Non presente"
                            size="S"
                            onClick={openUpdateModal}
                            expanded
                          />
                        );
                      }

                      if (isSaving) {
                        return <UserPreviewSkeleton size="S" />;
                      }

                      return (
                        <ViewAgentSmallPreview
                          agentIdOrEmail={jobApplication.referralEmail}
                          size="S"
                          onClick={openUpdateModal}
                          expanded
                        />
                      );
                    }}
                  </UpdateAssignedAgentAction>
                )}

                {canUpdateReferral && jobApplication.applicationType === JobApplicationType.GENERIC && (
                  <UpdateAssignedUserAction
                    label="Segnalatore"
                    aria-label="Campo per inserire il segnalatore"
                    email={jobApplication.referralEmail}
                    isRequired
                    onUpdate={({ email }) => updateJobApplication(id!, {
                      ...jobApplication,
                      referralEmail: email,
                    })}
                    onSuccess={() => mutateJobApplication()}
                  >
                    {({ openUpdateModal, isSaving }) => {
                      if (!jobApplication.referralEmail) {
                        return (
                          <UserPreview
                            name="Non presente"
                            size="S"
                            onClick={openUpdateModal}
                            expanded
                          />
                        );
                      }

                      if (isSaving) {
                        return <UserPreviewSkeleton size="S" />;
                      }

                      return (
                        <UserPreview
                          aria-label="user preview editabile"
                          name={getUserName(jobApplication.referralEmail) || 'Non presente'}
                          size="S"
                          onClick={openUpdateModal}
                          expanded
                        />
                      );
                    }}
                  </UpdateAssignedUserAction>
                )}

                {!canUpdateReferral && jobApplication.applicationType === JobApplicationType.SALES && (
                  <ViewAgentSmallPreview
                    agentIdOrEmail={jobApplication.referralEmail}
                    size="S"
                    expanded
                  />
                )}
                {!canUpdateAssignee && jobApplication.applicationType === JobApplicationType.GENERIC && (
                  <UserPreview
                    aria-label="user preview non editabile"
                    name={getUserName(jobApplication.referralEmail) || 'Non presente'}
                    size="S"
                    expanded
                  />
                )}
              </DetailItemList.Item>
            </DetailItemList>
          </Card.Content>

          {canViewApplicant && (
            <>
              <Card.Divider />

              {isApplicantLoading && (
                <CardSkeleton
                  aria-label="Informazioni candidato in caricamento"
                  asContent="padded"
                />
              )}

              {applicantError && (
                <Card
                  aria-label="Errore nel caricamento del candidato"
                  asContent="padded"
                >
                  <Card.Header
                    title="Candidato"
                    icon={{ path: ICON_ALERT_CIRCLE_OUTLINE }}
                    color="critical"
                  />
                  <Card.Content>
                    <Message message={LOAD_APPLICANT_ERROR_MESSAGE} />
                  </Card.Content>
                </Card>
              )}

              {applicant && (
                <Card
                  aria-label="Informazioni candidato"
                  asContent="padded"
                >
                  <Card.Header
                    title="Candidato"
                    primaryActions={canManageApplicant ? [
                      <UpdateApplicantAction
                        applicant={applicant}
                        onSuccess={() => mutateApplicant()}
                      />,
                    ] : undefined}
                  />
                  <Card.Content>
                    <Stack gap={200}>
                      <DetailItemList>
                        <DetailItemList.Item
                          label="Nome"
                          aria-label="Nome candidato"
                        >
                          {applicant.name}
                        </DetailItemList.Item>

                        <DetailItemList.Item
                          label="Telefono"
                          aria-label="Telefono candidato"
                        >
                          {applicant.phoneNumber}
                        </DetailItemList.Item>

                        <DetailItemList.Item
                          label="Email"
                          aria-label="Email candidato"
                          ellipsis
                        >
                          {applicant.email}
                        </DetailItemList.Item>
                      </DetailItemList>
                      <UserActions
                        user={{
                          phoneNumber: applicant.phoneNumber,
                          email: applicant.email,
                          linkedIn: applicant.linkedIn,
                        }}
                        hideLinkedInAction={false}
                      />
                    </Stack>
                  </Card.Content>
                </Card>
              )}
            </>
          )}
        </Card>
      )}
      sideSecondaryContent={(
        <Stack gap={300}>
          {applicant && showCalendarTools && (
            <ApplicantMissedCallRegistry
              missedCalls={applicant.missedCalls}
            />
          )}

          <ApplicantOptInsWidget
            applicantId={jobApplication.applicantId}
            canAdd={userIsAdmin || userIsHR}
            canDelete={userIsAdmin || userIsHR}
            canSeeDetails={userIsAdmin || userIsHR}
          />
        </Stack>
      )}
      footer={showCalendarTools && (
        <HStack>
          {applicant && (
            <NoAnswerAction
              applicant={applicant}
              jobApplication={jobApplication}
              onSuccess={() => {
                mutateApplicant();
                mutateJobApplication();
              }}
            >
              {({ onClick, isLoading: noAnswerLoading }) => (
                <Action
                  label="Non risponde"
                  iconLeft={{ path: ICON_PHONE_OFF_OUTLINE }}
                  onClick={onClick}
                  emphasis="high"
                  loading={noAnswerLoading}
                  aria-label="Azione per registrare che il candidato non ha risposto alla chiamata"
                />
              )}
            </NoAnswerAction>
          )}
          <UpsertHRRecallAction
            jobApplication={jobApplication}
            canCreate={canManageCalendarEvents}
            onSuccess={() => {
              mutateCalendarEvents();
              mutateJobApplication();
            }}
          >
            {({ openModal, isSaving }) => (
              <Action
                label="Fissa recall"
                iconRight={{ path: ICON_PHONE_OUTLINE }}
                onClick={openModal}
                emphasis="high"
                loading={isSaving}
                aria-label="Azione per fissare una recall"
              />
            )}
          </UpsertHRRecallAction>
          <UpsertHRInterviewAction
            jobApplication={jobApplication}
            canCreate={canManageCalendarEvents}
            onSuccess={() => {
              mutateCalendarEvents();
              mutateJobApplication();
            }}
          >
            {({ openModal, isSaving }) => (
              <Action
                label="Fissa colloquio"
                iconRight={{ path: ICON_CALENDAR_OUTLINE }}
                onClick={openModal}
                emphasis="high"
                loading={isSaving}
                aria-label="Azione per fissare il colloquio"
              />
            )}
          </UpsertHRInterviewAction>
        </HStack>
      )}
    >
      <NoticeBoard
        notices={notices}
        isLoading={areNoticesDataLoading}
        errors={noticesDataErrors}
      />
      <Card aria-label="Dettagli candidatura">
        <Card.Header
          title="Dettagli"
          icon={{ path: ICON_BRIEFCASE_OUTLINE }}
          primaryActions={[
            <UpdateJobApplicationAction
              jobApplication={jobApplication}
              onSuccess={() => mutateJobApplication()}
            />,
          ]}
        />
        <Card.Content>
          <DetailItemList columns={2}>
            <DetailItemList.Item label="Tipologia">
              {jobApplicationTypeLabels[jobApplication.applicationType]}
            </DetailItemList.Item>
            <DetailItemList.Item label="Posizione">
              {jobPosition.name}
            </DetailItemList.Item>
            <DetailItemList.Item label="Esperienza">
              {jobApplication.experience || '-'}
            </DetailItemList.Item>
            <DetailItemList.BooleanItem
              label="Attualmente lavora?"
              value={jobApplication.isEmployed || false}
            />
            {jobApplication.applicationType === JobApplicationType.SALES && (
              <>
                <DetailItemList.Item label="Luogo">
                  {jobApplication.provinces?.join(', ') || '-'}
                </DetailItemList.Item>
                <DetailItemList.BooleanItem
                  label="Patentino?"
                  value={jobApplication.hasLicense || false}
                />
                {jobApplication.hasLicense ? (
                  <DetailItemList.Item label="Codice patentino">
                    {jobApplication.licenseId || '-'}
                  </DetailItemList.Item>
                ) : <></>}
              </>
            )}
          </DetailItemList>
        </Card.Content>
      </Card>

      <HRCalendarEventsWidget
        jobApplication={jobApplication}
        canSetInterview={canManageCalendarEvents}
        canUpdateInterview={canManageCalendarEvents}
        canSetRecall={canManageCalendarEvents}
        canUpdateRecall={canManageCalendarEvents}
        canDeleteRecall={canManageCalendarEvents}
        onSet={() => mutateJobApplication()}
      />

      <NotesWidget
        reference={jobApplication}
        referenceType={ReferenceType.JOB_APPLICATION}
        onNotesUpdate={() => mutateJobApplication()}
      />
    </RightColumnPageLayout>
  );
};

export default ViewJobApplicationPage;
